/**
 * AiChatThinkingBlock — Collapsible thinking block within assistant messages.
 *
 * Collapsed by default, shows a preview of the first ~80 chars.
 * Pulse animation while streaming.
 */

import { useState } from 'react';
import { ChevronRight, Brain } from 'lucide-react';

type AiChatThinkingBlockProps = {
  thinking: string;
  isStreaming?: boolean;
};

export function AiChatThinkingBlock({ thinking, isStreaming }: AiChatThinkingBlockProps) {
  const [expanded, setExpanded] = useState(false);
  const preview = thinking.length > 80 ? thinking.slice(0, 80) + '...' : thinking;

  return (
    <div
      className={`my-1 rounded-md border text-xs text-muted-foreground bg-muted/30${isStreaming ? ' animate-pulse' : ''}`}
      data-testid="thinking-block"
    >
      <button
        className="flex items-center gap-1.5 w-full px-2 py-1 text-left"
        onClick={() => setExpanded(!expanded)}
        data-testid="thinking-block-toggle"
      >
        <ChevronRight
          className={`h-3 w-3 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
        <Brain className="h-3 w-3 shrink-0" />
        <span className="italic truncate">
          {expanded ? 'Thinking' : preview}
        </span>
      </button>

      {expanded && (
        <div className="px-2 pb-2 border-t text-[11px]">
          <p className="whitespace-pre-wrap mt-1 italic" data-testid="thinking-block-content">
            {thinking}
          </p>
        </div>
      )}
    </div>
  );
}
